import { useState, useCallback } from 'react';
import axios from 'axios';
import { User, EditingId, EditingPharmacy } from '../types';
import { API_ENDPOINTS } from '../constants';

export const useInlineEditing = (
  users: User[],
  showNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void,
  logAction: (action: string) => void,
  refetchUsers: () => void
) => {
  const [editingId, setEditingId] = useState<EditingId | null>(null);
  const [editingPharmacy, setEditingPharmacy] = useState<EditingPharmacy | null>(null);
  const [previousId, setPreviousId] = useState<string>('');
  const [previousPharmacyNum, setPreviousPharmacyNum] = useState<string>('');

  const canEditId = useCallback((userRole: string) => {
    const currentRole = localStorage.getItem('role');
    if (currentRole === 'admin') return true;
    if (currentRole === 'moder') return userRole === 'user';
    return false;
  }, []);

  const canEditPharmacyNum = useCallback((userRole: string) => {
    const currentRole = localStorage.getItem('role');
    if (currentRole === 'admin') return true;
    if (currentRole === 'moder') return userRole === 'user';
    return false;
  }, []);

  const startEditingId = useCallback((user: User) => {
    if (!canEditId(user.role)) return;
    setEditingId({
      userId: user.id,
      newId: user.id.toString()
    });
    setPreviousId(user.id.toString());
  }, [canEditId]);

  const startEditingPharmacy = useCallback((user: User) => {
    if (!canEditPharmacyNum(user.role)) return;
    setEditingPharmacy({
      userId: user.id,
      newPharmacyNum: user.pharmacyNum.toString()
    });
    setPreviousPharmacyNum(user.pharmacyNum.toString());
  }, [canEditPharmacyNum]);

  const saveNewId = useCallback(async (userId: number, newId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (newId === previousId) {
      setEditingId(null);
      return;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.CHANGE_USER_ID, {
        oldId: userId,
        newId: parseInt(newId)
      });

      if (response.data.success) {
        showNotification('ID пользователя успешно изменен', 'success');
        logAction(`изменил ID пользователя "${user.username}" с ${userId} на ${newId} | Пользователи`);
        refetchUsers();
        setEditingId(null);
      }
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        showNotification('Этот ID уже используется', 'error');
        setEditingId(prev => prev ? { ...prev, newId: previousId } : null);
      } else {
        console.error('Ошибка при изменении ID:', error);
        showNotification('Не удалось изменить ID пользователя', 'error');
        setEditingId(prev => prev ? { ...prev, newId: previousId } : null);
      }
    }
  }, [users, previousId, showNotification, logAction, refetchUsers]);

  const saveNewPharmacyNum = useCallback(async (userId: number, newPharmacyNum: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (newPharmacyNum === previousPharmacyNum) {
      setEditingPharmacy(null);
      return;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.CHANGE_PHARMACY_NUM, {
        userId: userId,
        newPharmacyNum: parseInt(newPharmacyNum)
      });

      if (response.data.success) {
        showNotification('Номер аптеки успешно изменен', 'success');
        logAction(`изменил номер аптеки пользователя "${user.username}" с ${user.pharmacyNum} на ${newPharmacyNum} | Пользователи`);
        refetchUsers();
        setEditingPharmacy(null);
      }
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        showNotification('Этот номер аптеки уже используется', 'error');
        setEditingPharmacy(prev => prev ? { ...prev, newPharmacyNum: previousPharmacyNum } : null);
      } else {
        console.error('Ошибка при изменении номера аптеки:', error);
        showNotification('Не удалось изменить номер аптеки', 'error');
        setEditingPharmacy(prev => prev ? { ...prev, newPharmacyNum: previousPharmacyNum } : null);
      }
    }
  }, [users, previousPharmacyNum, showNotification, logAction, refetchUsers]);

  return {
    editingId,
    editingPharmacy,
    canEditId,
    canEditPharmacyNum,
    startEditingId,
    startEditingPharmacy,
    saveNewId,
    saveNewPharmacyNum,
    setEditingId,
    setEditingPharmacy
  };
};

